import React from 'react';
import { saveAs } from 'file-saver';
import { Document, Packer, Paragraph, Table, TableRow, TableCell, HeadingLevel } from 'docx';
import './ExportButton.css'

const ExportButton = ({ schedule, residents, isScheduleGenerated, startDate, endDate }) => {
  const calculateStats = () => {
    const totalDays = Math.round((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24)) + 1;
    const stats = {};

    residents.forEach(resident => {
      const residentShifts = Object.values(schedule).filter(name => name === resident.name).length;
      const actualFrequency = residentShifts > 0 ? (totalDays / residentShifts).toFixed(2) : 'N/A';
      
      const residentDates = Object.entries(schedule)
        .filter(([_, name]) => name === resident.name)
        .map(([date, _]) => new Date(date));
      
      let avgDaysBetweenCalls = 'N/A';
      if (residentDates.length > 1) {
        const daysBetweenCalls = residentDates
          .sort((a, b) => a - b)
          .reduce((acc, date, i, arr) => {
            if (i === 0) return acc;
            return acc + (date - arr[i - 1]) / (1000 * 60 * 60 * 24);
          }, 0);
        avgDaysBetweenCalls = (daysBetweenCalls / (residentDates.length - 1)).toFixed(2);
      }

      stats[resident.name] = {
        totalShifts: residentShifts,
        targetFrequency: resident.max1inX,
        actualFrequency,
        avgDaysBetweenCalls
      };
    });

    return stats;
  };

  const handleTXTExport = () => {
    const stats = calculateStats();
    let content = "On Call Schedule\n\n";
    content += Object.entries(schedule)
      .map(([date, name]) => `${date}: ${name}`)
      .join('\n');
    
    content += "\n\nResident Statistics\n\n";
    Object.entries(stats).forEach(([name, stat]) => {
      content += `${name}:\n`;
      content += `  Total Shifts: ${stat.totalShifts}\n`;
      content += `  Target Frequency: 1 in ${stat.targetFrequency}\n`;
      content += `  Actual Frequency: ${stat.actualFrequency === 'N/A' ? 'N/A' : `1 in ${stat.actualFrequency}`}\n`;
      content += `  Avg Days Between Calls: ${stat.avgDaysBetweenCalls}\n\n`;
    });

    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    saveAs(blob, 'call_schedule.txt');
  };

  const handleDOCXExport = async () => {
    const stats = calculateStats();
    const doc = new Document({
      sections: [
        {
          properties: {},
          children: [
            new Paragraph({
              text: "On Call Schedule",
              heading: HeadingLevel.HEADING_1,
            }),
            new Table({
              width: { size: 100, type: 'pct' },
              rows: [
                new TableRow({
                  children: [
                    new TableCell({ children: [new Paragraph("Date")] }),
                    new TableCell({ children: [new Paragraph("On Call")] }),
                  ],
                }),
                ...Object.entries(schedule).map(([date, name]) => 
                  new TableRow({
                    children: [
                      new TableCell({ children: [new Paragraph(date)] }),
                      new TableCell({ children: [new Paragraph(name)] }),
                    ],
                  })
                ),
              ],
            }),
            new Paragraph({
              text: "Resident Statistics",
              heading: HeadingLevel.HEADING_1,
              break: true,
            }),
            new Table({
              width: { size: 100, type: 'pct' },
              rows: [
                new TableRow({
                  children: [
                    new TableCell({ children: [new Paragraph("Name")] }),
                    new TableCell({ children: [new Paragraph("Total Shifts")] }),
                    new TableCell({ children: [new Paragraph("Target Frequency")] }),
                    new TableCell({ children: [new Paragraph("Actual Frequency")] }),
                    new TableCell({ children: [new Paragraph("Avg Days Between Calls")] }),
                  ],
                }),
                ...Object.entries(stats).map(([name, stat]) => 
                  new TableRow({
                    children: [
                      new TableCell({ children: [new Paragraph(name)] }),
                      new TableCell({ children: [new Paragraph(stat.totalShifts.toString())] }),
                      new TableCell({ children: [new Paragraph(`1 in ${stat.targetFrequency}`)] }),
                      new TableCell({ children: [new Paragraph(stat.actualFrequency === 'N/A' ? 'N/A' : `1 in ${stat.actualFrequency}`)] }),
                      new TableCell({ children: [new Paragraph(stat.avgDaysBetweenCalls)] }),
                    ],
                  })
                ),
              ],
            }),
          ],
        },
      ],
    });

    Packer.toBlob(doc).then(blob => {
      saveAs(blob, 'call_schedule.docx');
    });
  };

  const handleICSExport = () => {
    let icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//hacksw/handcal//NONSGML v1.0//EN',
    ];

    Object.entries(schedule).forEach(([date, resident]) => {
      const [year, month, day] = date.split('-');
      const nextDay = new Date(year, month - 1, parseInt(day) + 1);
      const endDate = `${nextDay.getFullYear()}${(nextDay.getMonth() + 1).toString().padStart(2, '0')}${nextDay.getDate().toString().padStart(2, '0')}`;

      icsContent = icsContent.concat([
        'BEGIN:VEVENT',
        `DTSTART:${year}${month}${day}`,
        `DTEND:${endDate}`,
        `SUMMARY:On Call: ${resident}`,
        `DESCRIPTION:${resident} is on call for this day.`,
        'END:VEVENT'
      ]);
    });

    icsContent.push('END:VCALENDAR');

    const blob = new Blob([icsContent.join('\r\n')], { type: 'text/calendar;charset=utf-8' });
    saveAs(blob, 'on-call-schedule.ics');
  };

  return (
    <div className="export-buttons">
      <button 
        className="export-button"
        onClick={handleICSExport} 
        disabled={!isScheduleGenerated}
      >
        Export .ics
      </button>
      <button 
        className="export-button"
        onClick={handleTXTExport} 
        disabled={!isScheduleGenerated}
      >
        Export .txt
      </button>
      <button 
        className="export-button"
        onClick={handleDOCXExport} 
        disabled={!isScheduleGenerated}
      >
        Export .docx
      </button>
    </div>
  );
};

export default ExportButton;